import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import WovenPattern from "../components/WovenPattern";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="relative flex min-h-[70vh] items-center justify-center overflow-hidden woven-texture px-5 py-20">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-60">
        <WovenPattern opacity={0.14} />
      </div>
      <div className="relative w-full max-w-md rounded-xl border border-white/10 bg-white p-8 shadow-2xl" data-testid="login-card">
        <span className="flex h-12 w-12 items-center justify-center rounded-md bg-melt-teal/10 text-melt-teal">
          <Lock size={24} aria-hidden="true" />
        </span>
        <h1 className="mt-5 font-serif text-2xl font-semibold text-melt-indigo">Admin sign in</h1>
        <p className="mt-2 text-[15px] text-melt-indigo/70">
          Restricted area for the MELT toolkit team to read enquiries.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-5" noValidate>
          {error && (
            <p role="alert" className="rounded-md border border-[#B42318]/30 bg-[#B42318]/5 px-4 py-3 text-sm font-medium text-[#B42318]" data-testid="login-error">
              {error}
            </p>
          )}
          <div>
            <label htmlFor="email" className="block text-[15px] font-semibold text-melt-indigo">Email</label>
            <input
              id="email" type="email" autoComplete="username" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full rounded-md border border-melt-grey bg-white px-4 py-3 text-base text-melt-indigo focus:border-melt-teal"
              data-testid="login-email"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-[15px] font-semibold text-melt-indigo">Password</label>
            <input
              id="password" type="password" autoComplete="current-password" required value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full rounded-md border border-melt-grey bg-white px-4 py-3 text-base text-melt-indigo focus:border-melt-teal"
              data-testid="login-password"
            />
          </div>
          <button
            type="submit" disabled={busy}
            className="w-full rounded-md bg-melt-teal px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-melt-teal-600 disabled:opacity-60"
            data-testid="login-submit"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </section>
  );
}
